/**
 * M5.8-real — display formatters for the platform-admin UI.
 *
 * AED is the only currency the platform deals in (Decision: AED only).
 * en-AE locale gives correct grouping (1,234,567.89) and currency
 * symbol placement.
 */
import i18next from 'i18next';

const AED_FORMATTER = new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const AED_FORMATTER_COMPACT = new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const INT_FORMATTER = new Intl.NumberFormat('en-AE', {
    maximumFractionDigits: 0,
});

export function formatAED(amount: number): string {
    if (!Number.isFinite(amount)) return AED_FORMATTER.format(0);
    return AED_FORMATTER.format(amount);
}

export function formatAEDCompact(amount: number): string {
    if (!Number.isFinite(amount)) return AED_FORMATTER_COMPACT.format(0);
    return AED_FORMATTER_COMPACT.format(amount);
}

export function formatPercent(value: number, digits = 1): string {
    if (!Number.isFinite(value)) return '0%';
    return `${value.toFixed(digits)}%`;
}

export function formatInt(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return INT_FORMATTER.format(n);
}

/*
 * UX-14 — one date/time formatter for the operator console.
 *
 * Dates render in the UI language (i18n.language, or an explicit `lang`)
 * and always in the platform time zone (Asia/Dubai) so two operators in
 * different browsers read the same wall-clock time. Missing or invalid
 * values render as an em dash.
 */
export const PLATFORM_TIME_ZONE = 'Asia/Dubai';
export const EMPTY_VALUE = '—';

type DateInput = string | number | Date | null | undefined;

const INTL_LOCALE: Record<string, string> = { en: 'en-AE', ar: 'ar-AE', es: 'es' };

function uiLocale(lang?: string): string {
    const code = (lang ?? i18next.resolvedLanguage ?? i18next.language ?? 'en').split('-')[0];
    return INTL_LOCALE[code] ?? code;
}

function toDate(value: DateInput): Date | null {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: DateInput, lang?: string): string {
    const date = toDate(value);
    if (!date) return EMPTY_VALUE;
    return new Intl.DateTimeFormat(uiLocale(lang), { dateStyle: 'medium', timeZone: PLATFORM_TIME_ZONE }).format(date);
}

export function formatDateTime(value: DateInput, lang?: string): string {
    const date = toDate(value);
    if (!date) return EMPTY_VALUE;
    return new Intl.DateTimeFormat(uiLocale(lang), {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: PLATFORM_TIME_ZONE,
    }).format(date);
}

/** "5 minutes ago" / "in 2 days" in the UI language. */
export function formatRelative(value: DateInput, lang?: string, now: number = Date.now()): string {
    const date = toDate(value);
    if (!date) return EMPTY_VALUE;
    const fmt = new Intl.RelativeTimeFormat(uiLocale(lang), { numeric: 'auto' });
    const diffSec = Math.round((date.getTime() - now) / 1000);
    if (Math.abs(diffSec) < 60) return fmt.format(diffSec <= 0 ? Math.min(-1, diffSec) : Math.max(1, diffSec), 'second');
    const diffMin = Math.trunc(diffSec / 60);
    if (Math.abs(diffMin) < 60) return fmt.format(diffMin, 'minute');
    const diffHr = Math.trunc(diffMin / 60);
    if (Math.abs(diffHr) < 24) return fmt.format(diffHr, 'hour');
    return fmt.format(Math.trunc(diffHr / 24), 'day');
}
